import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { purchaseAPI } from '../api';
import toast from 'react-hot-toast';

export default function CheckoutPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { event, ticketType, quantity } = location.state || {};

  const [isMember, setIsMember] = useState(null);
  const [form, setForm] = useState({ buyer_name: '', buyer_email: '', buyer_phone: '', buyer_id_number: '', member_number: '' });
  const [loading, setLoading] = useState(false);
  const [paymentSent, setPaymentSent] = useState(false);
  const [purchaseId, setPurchaseId] = useState(null);

  // If no state, redirect back
  useEffect(() => { if (!event) navigate(`/event/${id}`); }, [event]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePay = async () => {
    if (!form.buyer_name || !form.buyer_email || !form.buyer_phone) {
      toast.error('Name, email and phone are required'); return;
    }
    if (isMember && !form.member_number) { toast.error('Enter your member number'); return; }
    if (!isMember && !form.buyer_id_number) { toast.error('Enter your ID/Passport number'); return; }
    setLoading(true);
    try {
      const res = await purchaseAPI.create({
        event_id: id,
        ticket_type_id: ticketType.id,
        quantity,
        ...form
      });
      setPurchaseId(res.data.purchase_id);
      toast.success('✅ M-Pesa prompt sent! Enter your PIN.');
      setPaymentSent(true);
      // Poll for completion
      const interval = setInterval(async () => {
        try {
          const check = await purchaseAPI.get(res.data.purchase_id);
          if (check.data.status === 'completed') {
            clearInterval(interval);
            toast.success('🎉 Payment confirmed! Your ticket is on its way!');
            setTimeout(() => navigate(`/ticket/${check.data.ticket_code}`), 1500);
          }
        } catch {}
      }, 5000);
      setTimeout(() => clearInterval(interval), 300000);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Payment failed. Try again.');
    } finally { setLoading(false); }
  };

  if (!event) return null;

  const total = ticketType.price * quantity;

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Header */}
      <div style={{ background: '#1A2744', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to={`/event/${id}`} style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>← Back</Link>
        <img src="https://omogusiiwarriors.org/icons/logo.png" alt="OWW" style={{ width: 32, height: 32, objectFit: 'contain' }} />
        <span style={{ color: '#fff', fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>Checkout</span>
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '32px 24px' }}>
        {/* Order Summary */}
        <div style={{ background: '#1A2744', borderRadius: 12, padding: 20, marginBottom: 24, color: '#fff' }}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Order Summary</div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, marginBottom: 8 }}>{event.name}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 12 }}>
            📅 {new Date(event.date).toLocaleDateString('en-KE', { dateStyle: 'long' })} · 📍 {event.venue}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 12 }}>
            <span>{ticketType.name} × {quantity}</span>
            <span style={{ fontWeight: 800, fontSize: 18, color: '#E8521A' }}>KSh {total.toLocaleString()}</span>
          </div>
        </div>

        {!paymentSent ? (
          <>
            {/* Member or non-member */}
            {isMember === null && (
              <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20 }}>
                <h3 style={{ margin: '0 0 16px', color: '#1A2744' }}>Are you an OWW Member?</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <button onClick={() => setIsMember(true)}
                    style={{ padding: 16, border: '2px solid #1A2744', borderRadius: 10, background: '#fff', cursor: 'pointer', fontWeight: 600, color: '#1A2744' }}>
                    ✅ Yes, I'm a Member
                  </button>
                  <button onClick={() => setIsMember(false)}
                    style={{ padding: 16, border: '2px solid #E5E7EB', borderRadius: 10, background: '#fff', cursor: 'pointer', fontWeight: 600, color: '#6B7280' }}>
                    👤 Non-Member
                  </button>
                </div>
              </div>
            )}

            {isMember !== null && (
              <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0, color: '#1A2744' }}>Your Details</h3>
                  <button onClick={() => setIsMember(null)} style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 13 }}>Change</button>
                </div>
                {[
                  { label: 'Full Name *', key: 'buyer_name', placeholder: 'As on your ID' },
                  { label: 'Email Address *', key: 'buyer_email', placeholder: 'Ticket will be sent here', type: 'email' },
                  { label: 'M-Pesa Phone *', key: 'buyer_phone', placeholder: 'e.g. 0712345678' },
                  isMember
                    ? { label: 'Member Number *', key: 'member_number', placeholder: 'e.g. OWW-0001' }
                    : { label: 'ID / Passport Number *', key: 'buyer_id_number', placeholder: 'National ID or Passport' }
                ].map(f => (
                  <div key={f.key} style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>{f.label}</label>
                    <input type={f.type || 'text'} placeholder={f.placeholder} value={form[f.key]}
                      onChange={e => set(f.key, e.target.value)}
                      style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14 }} />
                  </div>
                ))}
                <button onClick={handlePay} disabled={loading}
                  style={{ width: '100%', background: '#2D7D32', color: '#fff', padding: 14, borderRadius: 10, border: 'none', fontWeight: 700, fontSize: 16, cursor: loading ? 'not-allowed' : 'pointer', marginTop: 8, opacity: loading ? 0.7 : 1 }}>
                  {loading ? '⏳ Sending M-Pesa prompt...' : `📱 Pay KSh ${total.toLocaleString()} via M-Pesa`}
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
            <h3 style={{ color: '#1A2744', marginBottom: 8 }}>Waiting for payment...</h3>
            <p style={{ color: '#6B7280' }}>Enter your M-Pesa PIN on your phone. Your ticket will be sent to <strong>{form.buyer_email}</strong> automatically.</p>
            <div style={{ background: '#FEF3C7', borderRadius: 8, padding: 16, marginTop: 16 }}>
              <p style={{ color: '#92400E', margin: 0, fontSize: 13 }}>This page will update automatically. Don't close it.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
